import UploadMapChanges from "@/components/map/UploadMapChanges";
import { MapEditSelectedZones } from "@cmp-src/map/InteractiveZones/MapEditSelectedZones";
import { IResource } from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import { useBooleanState } from "@gettactic/helpers/src";
import { UseSvgMapInteractionsReturn } from "@gettactic/helpers/src/map/svg/useSvgMapInteractions";
import { FullPageLoader } from "@v2/components/loaders/FullPageLoader/FullPageLoader";
import { useMap } from "@webV2/map/MapProvider";
import { MapViewSvgFloorItem } from "@webV2/map/MapView/FloorItem/MapViewSvgFloorItem";
import { MapInteractiveZones } from "@webV2/map/MapView/MapInteractiveZones";
import { MapStackSwitcher } from "@webV2/map/MapView/MapStackSwitcher";
import { MapViewOverlays } from "@webV2/map/MapView/MapViewOverlays";
import { UseMapViewSvgEditReturn } from "@webV2/map/MapView/MapViewSvg/useMapViewSvgEdit";
import { useOnMapLoaded } from "@webV2/map/MapView/useOnMapLoaded";
import { useLayout } from "@webV2/providers/LayoutProvider/context";
import { useReserve } from "@webV2/providers/ReserveProvider/context";
import React from "react";

const MAP_CONTAINER_TOP_BOUNDARY = 0;

const classes = {
  FloorsContainer:
    "absolute top-24 md:top-0 right-0 bottom-0 left-0 md:left-[425px]",
  FloorsContainerEmbedded: "absolute top-0 md:top-0 right-0 bottom-0 left-0",
  FloorsContainerEditing: "absolute top-0 right-0 bottom-0 left-0",
  PerspectiveContainer: "h-full w-full overflow-hidden"
};

export interface MapViewProps {
  embedded?: boolean;
  editor?: UseMapViewSvgEditReturn;
  allowFloorSwitcher?: boolean;
  allowOverlays?: boolean;
  initialFocussedResource?: string;
  showMiniMap?: boolean;
  allowInteractionsOnFocussedResource?: boolean;
  isOnReservationModal?: boolean;
  isReservationCardIsOpen?: boolean;
}

export const MapViewSvg: React.FC<MapViewProps> = ({
  embedded,
  editor,
  allowFloorSwitcher = true,
  allowOverlays = true,
  initialFocussedResource,
  showMiniMap = true,
  isOnReservationModal: propsIsOnReservationModal = false,
  isReservationCardIsOpen: propsIsReservationCardIsOpen = false,

  allowInteractionsOnFocussedResource = false
}) => {
  const {
    floor,
    selectedResource,
    setOpenResourceModal,
    poppers,
    floors,
    zonesInfo,
    mapChangesCount,
    setMapChangesCount,
    setFloor,
    events,
    fullscreenMapDisclosure
  } = useMap();

  const { isReserveOpen } = useReserve();

  const isMapEditing =
    editor?.isResourcePanelEditorOpen || editor?.isMapBuilderEditorOpen;

  const [mapContainerRef, setMapContainerRef] =
    React.useState<HTMLDivElement | null>(null);

  const { setSelectedFloor: setLayoutFloor } = useLayout();

  const floorContainer = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<HTMLDivElement | null>(null);
  const svgRef = React.useRef<SVGSVGElement | null | undefined>(null);
  const isMapLoaded = useBooleanState();
  const isMaLoadedRef = React.useRef(false);
  const isMapSwitcherReady = useBooleanState();
  const isSwitchingWithFloorSwitcher = useBooleanState();

  const svgMapInteractions = React.useRef<UseSvgMapInteractionsReturn | null>(
    null
  );
  const [initFocusResource, setInitFocusResource] = React.useState<
    string | undefined
  >(initialFocussedResource);
  const [currentFloor, setCurrentFloor] = React.useState<IResource | null>(
    floor
  );

  const isChangingFloor = floor?.id !== currentFloor?.id;

  const {
    onMapLoaded,
    onMapUnloaded,
    isLoading: isMapLoaderRunning
  } = useOnMapLoaded(floor, (svg) => {
    svgRef.current = svg;
  });

  React.useEffect(() => {
    onMapUnloaded();
    const timeout = setTimeout(() => {
      setCurrentFloor(floor);
      isMapLoaded.false();
      isMaLoadedRef.current = false;
      isMapSwitcherReady.false();
    }, 500);

    return () => clearTimeout(timeout);
  }, [floor?.id]);

  const availableFloors =
    floors?.filter((x) => !!x.map_url || !!x.map_data) || [];

  const updateModalPosition = () => {
    if (!poppers.updateResDetails || !poppers.referenceResDetailsElement) {
      return;
    }
    if (
      poppers.referenceResDetailsElement.getBoundingClientRect().top <
      MAP_CONTAINER_TOP_BOUNDARY
    ) {
      setOpenResourceModal(false);
    }
    if (selectedResource) {
      poppers.updateResDetails();
    }
  };

  const containerBounds = React.useMemo(() => {
    return mapContainerRef
      ? mapContainerRef.getBoundingClientRect()
      : { top: 0, left: 0, width: 0, height: 0 };
  }, [mapContainerRef]);

  const itemContainerRef = React.useRef<HTMLDivElement | null>(null);
  const isFloorLoading = isChangingFloor || !isMapLoaded.state;

  React.useEffect(() => {
    if (!isFloorLoading) isSwitchingWithFloorSwitcher.false();
  }, [isFloorLoading]);

  // Recenter map when reservation modal state changes (drawer hide/show)
  // But don't blur the focused resource if the reserve modal is open
  React.useEffect(() => {
    if (!isMaLoadedRef.current) return;

    // Don't trigger blur if reserve modal is open - this prevents
    // the modal from closing when exiting fullscreen to make a reservation
    if (isReserveOpen) return;

    const timeout = setTimeout(() => {
      events.trigger("onFocusResource", null);
    }, 500);

    return () => clearTimeout(timeout);
  }, [fullscreenMapDisclosure.isOpen, isReserveOpen]);

  const handleMapReady = () => {
    if (isMaLoadedRef.current) return;

    isMaLoadedRef.current = true;
    isMapLoaded.true();

    if (initFocusResource) {
      setTimeout(() => {
        events.trigger("onFocusResource", {
          id: initFocusResource
        } as IResource);
        setInitFocusResource(undefined);
      }, 500);
    }
  };

  if (!availableFloors) return <div />;

  const isAnyFloorSelected = !!currentFloor;
  const showFloorSwitcher = !currentFloor && !floor;

  return (
    <>
      <div className={"fixed top-0 left-1/2 z-modal p-2"}>
        {!!mapChangesCount && !!svgRef.current && !!currentFloor && (
          <UploadMapChanges
            floor={currentFloor}
            svg={svgRef.current}
            mapChanges={mapChangesCount}
            setMapChanges={setMapChangesCount}
          />
        )}
      </div>
      <div className={"fixed top-0 left-1/2 z-modal p-2"}>
        {!!mapChangesCount && !!svgRef.current && !!currentFloor && (
          <UploadMapChanges
            floor={currentFloor}
            svg={svgRef.current}
            mapChanges={mapChangesCount}
            setMapChanges={setMapChangesCount}
          />
        )}
      </div>
      <div
        onClick={updateModalPosition}
        className={"floor-container"}
        ref={floorContainer}
      >
        <div className={"v2-map"}>
          {allowFloorSwitcher &&
          (showFloorSwitcher || isSwitchingWithFloorSwitcher.state) ? (
            <MapStackSwitcher
              floorContainerRef={floorContainer}
              onReady={() => isMapSwitcherReady.true()}
              onFloorSelected={(floor) => {
                isSwitchingWithFloorSwitcher.true();
                setFloor(floor);
              }}
            />
          ) : undefined}
          {currentFloor ? (
            <div
              className={cn(
                "map-container",
                "transition-opacity",
                isFloorLoading ? "opacity-0" : "opacity-100",
                isMapEditing
                  ? classes.FloorsContainerEditing
                  : embedded
                    ? classes.FloorsContainerEmbedded
                    : classes.FloorsContainer,
                fullscreenMapDisclosure.isOpen ? "md:left-0" : ""
              )}
              ref={setMapContainerRef}
            >
              {!!mapContainerRef && !!floorContainer.current && (
                <div
                  className={classes.PerspectiveContainer}
                  style={{
                    perspective: "1200px"
                  }}
                >
                  <div
                    className={"item-container"}
                    style={{
                      width: containerBounds.width,
                      height: containerBounds.height,
                      transformStyle: "preserve-3d",
                      transition: !isAnyFloorSelected
                        ? "transform 0.8s ease"
                        : undefined,

                      pointerEvents: "none"
                    }}
                    ref={itemContainerRef}
                  >
                    <MapViewSvgFloorItem
                      allowInteractionsOnFocussedResource={
                        allowInteractionsOnFocussedResource
                      }
                      showMiniMap={showMiniMap}
                      floor={currentFloor}
                      mapRef={mapRef}
                      key={currentFloor.id}
                      index={
                        floors?.findIndex((f) => f.id === currentFloor.id) ?? -1
                      }
                      isVisible={true}
                      isMapEditing={
                        editor?.isResourcePanelEditorOpen ||
                        editor?.isMapBuilderEditorOpen
                      }
                      floorContainerRef={floorContainer}
                      mapContainerRef={{ current: mapContainerRef }}
                      onMapReady={(floor, svg, mapInteractions) => {
                        //if (floor?.id === currentFloor?.id) return;
                        if (isMapLoaded.state) return;
                        svgMapInteractions.current = mapInteractions;
                        onMapLoaded(null, svg);
                        setLayoutFloor(floor);
                      }}
                      onMapReadyEditing={(_, svg) => {
                        editor?.startEdit({ current: svg });
                      }}
                    >
                      {!!currentFloor &&
                        (editor?.isResourcePanelEditorOpen ? (
                          <MapEditSelectedZones
                            selectedZones={editor.selectedNodes}
                            zonesInfo={zonesInfo}
                            className={"absolute top-0 left-0"}
                          />
                        ) : (
                          <MapInteractiveZones
                            svgRef={svgRef}
                            onReady={handleMapReady}
                          />
                        ))}
                    </MapViewSvgFloorItem>
                  </div>
                </div>
              )}
            </div>
          ) : undefined}
        </div>
      </div>
      {allowOverlays && (
        <MapViewOverlays
          zonesInfo={zonesInfo}
          svgMapRef={svgRef}
          mapContainerRef={{ current: mapContainerRef }}
        />
      )}
      <FullPageLoader
        className={
          embedded ? classes.FloorsContainerEmbedded : classes.FloorsContainer
        }
        isLoading={
          showFloorSwitcher ? !isMapSwitcherReady.state : isFloorLoading
        }
      />
    </>
  );
};
